import type { FieldDefinition, RichTextValue } from "@/types/template";
import { SliderInput } from "./fields/SliderInput";
import { KnobInput } from "./fields/KnobInput";
import { ColorAlphaInput } from "./fields/ColorAlphaInput";
import { SUPPORTED_FONTS, getFontCapabilities } from "@/lib/fonts";
import { useTenantFonts } from "@/lib/tenantFonts";

const DEFAULT_RICH_TEXT: RichTextValue = {
  text: "",
  font: "Rubik",
  fontSize: 64,
};

function asRichText(value: unknown, fallback: RichTextValue): RichTextValue {
  if (value && typeof value === "object" && "text" in value) {
    const v = value as Partial<RichTextValue>;
    // Bold/italic/underline are optional — undefined → leave undefined so the
    // serialized payload stays identical to pre-feature templates. We never
    // synthesize `false` because that would make every template's saved JSON
    // bloat by three keys after a single edit pass.
    const next: RichTextValue = {
      text: typeof v.text === "string" ? v.text : fallback.text,
      font: typeof v.font === "string" ? v.font : fallback.font,
      fontSize: typeof v.fontSize === "number" ? v.fontSize : fallback.fontSize,
    };
    if (typeof v.bold === "boolean") next.bold = v.bold;
    if (typeof v.italic === "boolean") next.italic = v.italic;
    if (typeof v.underline === "boolean") next.underline = v.underline;
    return next;
  }
  // Backward compat: if a field was migrated from "text" but the props still
  // hold a string, treat it as the text portion with default font/size.
  if (typeof value === "string") {
    return { ...fallback, text: value };
  }
  return fallback;
}

/**
 * Toggle a flag on a RichTextValue. When the next value is the default
 * (undefined / false), the key is REMOVED rather than set to false — this
 * keeps the on-disk JSON minimal and means a template that never used
 * formatting serializes to the exact same bytes as pre-feature.
 */
function toggleRichFlag(
  rich: RichTextValue,
  key: "bold" | "italic" | "underline",
): RichTextValue {
  const current = Boolean(rich[key]);
  const next: RichTextValue = { ...rich };
  if (current) {
    delete next[key];
  } else {
    next[key] = true;
  }
  return next;
}

interface FieldEditorProps {
  fields: FieldDefinition[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}

export function FieldEditor({ fields, values, onChange }: FieldEditorProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {fields.map((field) => (
        <FieldInput
          key={field.key}
          field={field}
          value={values[field.key] ?? field.default}
          onChange={(v) => onChange(field.key, v)}
        />
      ))}
    </div>
  );
}

interface FieldInputProps {
  field: FieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
}

function FieldInput({ field, value, onChange }: FieldInputProps) {
  // Tenant brand fonts, merged into the text-rich picker below. Subscribed
  // unconditionally (hooks rule) even for non-typography fields — the store is
  // trivial and empty until the user has uploaded any.
  const tenantFonts = useTenantFonts();
  const tenantFamilies = Array.from(new Set(tenantFonts.map((f) => f.family)));

  const labelEl = (
    <label className="mb-1 block text-xs font-medium text-ink-2">
      {field.label}
      {field.required && <span className="ml-1 text-danger">*</span>}
    </label>
  );

  const inputClasses =
    "w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink transition-colors focus:border-line-strong focus:outline-none";

  switch (field.type) {
    case "text":
      return (
        <div>
          {labelEl}
          <input
            type="text"
            value={String(value ?? "")}
            onChange={(e) => onChange(e.target.value)}
            className={inputClasses}
          />
        </div>
      );

    case "textarea":
      return (
        <div className="sm:col-span-2 lg:col-span-3">
          {labelEl}
          <textarea
            value={String(value ?? "")}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className={`${inputClasses} resize-none`}
          />
        </div>
      );

    case "text-rich": {
      const fallback =
        field.default && typeof field.default === "object"
          ? (field.default as RichTextValue)
          : DEFAULT_RICH_TEXT;
      const rich = asRichText(value, fallback);
      const minSize = field.min ?? 8;
      const maxSize = field.max ?? 256;
      // Capabilities depend on the SELECTED font — switching to Bebas Neue
      // (which only ships 400) disables the bold toggle, switching back to
      // Rubik re-enables it. This is the entire point of option (b): the UI
      // never offers a format that would produce a browser-faked / squished
      // glyph.
      const caps = getFontCapabilities(rich.font);
      const formatBtn = (args: {
        active: boolean;
        disabled: boolean;
        title: string;
        onClick: () => void;
        children: React.ReactNode;
        className?: string;
      }) => (
        <button
          type="button"
          onClick={args.onClick}
          disabled={args.disabled}
          title={args.title}
          aria-pressed={args.active}
          className={`flex h-8 w-8 items-center justify-center rounded-lg border text-sm transition-colors ${
            args.disabled
              ? "border-line bg-surface-2 text-ink-3 cursor-not-allowed"
              : args.active
                ? "border-accent bg-accent-soft text-accent"
                : "border-line bg-surface text-ink-2 hover:bg-surface-3 hover:text-ink"
          } ${args.className ?? ""}`}
        >
          {args.children}
        </button>
      );
      return (
        <div className="sm:col-span-2 lg:col-span-3">
          {labelEl}
          <div className="space-y-2">
            <div className="flex items-stretch gap-2">
              {field.multiline ? (
                // Native resize grip (bottom-right), vertical only so the
                // toolbar row can't be pushed out of the panel.
                <textarea
                  value={rich.text}
                  onChange={(e) => onChange({ ...rich, text: e.target.value })}
                  className={`${inputClasses} min-h-[4.5rem] flex-1 resize-y`}
                  placeholder="Text content"
                  rows={3}
                />
              ) : (
                <input
                  type="text"
                  value={rich.text}
                  onChange={(e) =>
                    onChange({ ...rich, text: e.target.value.replace(/\n/g, " ") })
                  }
                  className={`${inputClasses} flex-1`}
                  placeholder="Text content"
                />
              )}
              <div
                className="flex items-center gap-1"
                role="group"
                aria-label="Text formatting"
              >
                {formatBtn({
                  active: Boolean(rich.bold),
                  disabled: !caps.bold,
                  title: caps.bold
                    ? "Bold"
                    : `${rich.font} doesn't ship a bold weight — pick a font with one to enable.`,
                  onClick: () => onChange(toggleRichFlag(rich, "bold")),
                  children: <span style={{ fontWeight: 700 }}>B</span>,
                })}
                {formatBtn({
                  active: Boolean(rich.italic),
                  disabled: !caps.italic,
                  title: caps.italic
                    ? "Italic"
                    : "Italic faces aren't loaded for any font yet — coming in a future update.",
                  onClick: () => onChange(toggleRichFlag(rich, "italic")),
                  children: <span style={{ fontStyle: "italic" }}>I</span>,
                })}
                {formatBtn({
                  active: Boolean(rich.underline),
                  // Underline is pure CSS — always available regardless of font.
                  disabled: false,
                  title: "Underline",
                  onClick: () => onChange(toggleRichFlag(rich, "underline")),
                  children: (
                    <span style={{ textDecoration: "underline" }}>U</span>
                  ),
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={rich.font}
                onChange={(e) => onChange({ ...rich, font: e.target.value })}
                className={inputClasses}
                title="Font family"
              >
                {/* Brand fonts group only appears once the tenant has uploaded
                    at least one — keeps the picker clean for everyone else. */}
                {tenantFamilies.length > 0 && (
                  <optgroup label="Brand fonts">
                    {tenantFamilies.map((family) => (
                      <option key={`tenant-${family}`} value={family}>
                        {family}
                      </option>
                    ))}
                  </optgroup>
                )}
                {SUPPORTED_FONTS.map((f) => (
                  <option key={f.family} value={f.family}>
                    {f.name}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={minSize}
                  max={maxSize}
                  step={1}
                  value={rich.fontSize}
                  onChange={(e) =>
                    onChange({ ...rich, fontSize: Number(e.target.value) })
                  }
                  className="flex-1 accent-accent"
                  title="Font size (px)"
                />
                <span className="text-xs text-ink-2 w-12 text-right">
                  {rich.fontSize}px
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    case "number":
      return (
        <div>
          {labelEl}
          <input
            type="number"
            value={Number(value ?? 0)}
            onChange={(e) => onChange(Number(e.target.value))}
            min={field.min}
            max={field.max}
            step={field.step}
            className={inputClasses}
          />
        </div>
      );

    case "slider":
      return (
        <SliderInput
          value={Number(value ?? field.min ?? 0)}
          onChange={(v) => onChange(v)}
          min={field.min ?? 0}
          max={field.max ?? 100}
          step={field.step ?? 1}
          label={field.label}
          required={field.required}
        />
      );

    case "knob":
      return (
        <KnobInput
          value={Number(value ?? field.min ?? 0)}
          onChange={(v) => onChange(v)}
          min={field.min ?? 0}
          max={field.max ?? 100}
          step={field.step ?? 1}
          label={field.label}
          required={field.required}
        />
      );

    case "color":
      return (
        <div>
          {labelEl}
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={String(value ?? "#000000")}
              onChange={(e) => onChange(e.target.value)}
              className="h-9 w-9 cursor-pointer rounded border border-line bg-transparent"
            />
            <input
              type="text"
              value={String(value ?? "")}
              onChange={(e) => onChange(e.target.value)}
              className={inputClasses}
              placeholder="#000000"
            />
          </div>
        </div>
      );

    case "color-alpha":
      return (
        <ColorAlphaInput
          value={String(value ?? "#000000ff")}
          onChange={(v) => onChange(v)}
          label={field.label}
          required={field.required}
        />
      );

    case "image":
      return (
        <div>
          {labelEl}
          <input
            type="text"
            value={String(value ?? "")}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Image URL"
            className={inputClasses}
          />
        </div>
      );

    case "select":
      return (
        <div>
          {labelEl}
          <select
            value={String(value ?? "")}
            onChange={(e) => onChange(e.target.value)}
            className={inputClasses}
          >
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      );

    case "boolean":
      return (
        <div className="flex items-center gap-2 pt-5">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded border-line bg-surface text-accent accent-accent"
          />
          <label className="text-xs font-medium text-ink-2">
            {field.label}
          </label>
        </div>
      );

    case "layer": {
      // Layer visibility: value !== false means visible (missing = on, so
      // templates from before a layer was added stay fully rendered).
      const visible = value !== false;
      return (
        <div className="flex items-center gap-2 pt-5">
          <button
            type="button"
            onClick={() => onChange(!visible)}
            aria-label={visible ? `Hide ${field.label}` : `Show ${field.label}`}
            className={`flex h-6 w-6 items-center justify-center rounded-md border transition-colors ${
              visible
                ? "border-line text-ink-2 hover:text-ink"
                : "border-line bg-surface-3 text-ink-3"
            }`}
          >
            {visible ? (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M3 3l18 18M10.6 10.6a3 3 0 004.2 4.2M9.9 4.6A10.9 10.9 0 0112 4.5c6.5 0 10 7.5 10 7.5a17.6 17.6 0 01-3.1 4.2M6.6 6.6C3.9 8.5 2 12 2 12s3.5 7 10 7c1.2 0 2.3-.2 3.3-.6" />
              </svg>
            )}
          </button>
          <label className={`text-xs font-medium ${visible ? "text-ink-2" : "text-ink-3 line-through"}`}>
            {field.label}
          </label>
        </div>
      );
    }

    default:
      return null;
  }
}
